import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

export default function SecurityArchitecture() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <section className="bg-[#032d28] text-white relative overflow-hidden flex flex-col items-center py-24 px-6 min-h-screen">
      {/* BACK BUTTON */}
      <Link
        to="/services/security-consulting-advisory"
        className="absolute top-20 left-8 text-white/40 hover:text-[#24ff81] transition-colors"
      >
        <ArrowLeft size={26} />
      </Link>

      <div className="max-w-3xl mx-auto text-left w-full">
        {/* IMAGE */}
        <div className="flex justify-center mb-10">
          <img
            src="https://static.wixstatic.com/media/nsplsh_5f353675726d716c5f6d51~mv2_d_3648_2736_s_4_2.jpg"
            alt="Security Architecture"
            className="w-44 h-44 rounded-full object-cover shadow-xl"
          />
        </div>

        {/* TITLE */}
        <h1 className="text-3xl md:text-4xl font-bold tracking-wide mb-6 uppercase text-center">
          Security Architecture
        </h1>

        {/* INTRO */}
        <p className="text-white/80 leading-relaxed mb-10">
          <b>Security architecture</b> is the principles, methods, tools, and
          frameworks that help protect an organisation’s assets from threats.
          Security architecture can be applied to an entire organisation, or to
          individual applications, processes, networks, cloud environments, or
          infrastructure.
        </p>

        {/* BENEFITS */}
        <h2 className="text-xl font-bold mb-4">
          The <b>benefits</b> of good security architecture are:
        </h2>

        <div className="space-y-4 text-white/80 mb-12">
          <p>
            <b>Increased Return on Investment (ROI)</b> from the cybersecurity
            budget by identifying business objectives and requirements, then
            translating these into secure architecture objectives and
            requirements.
          </p>

          <p>
            <b>Provides visibility and insight</b> to cybersecurity risks
            resulting from service and technology usage by creating traceability
            between security and business requirements.
          </p>

          <p>
            <b>Reduced project costs</b> by identifying and planning
            cybersecurity requirements into the early stages of projects, rather
            than attempting to ‘bolt on’ controls after the project is
            delivered.
          </p>
        </div>

        {/* APPROACH */}
        <h2 className="text-xl font-bold mb-4">Our approach</h2>
        <p className="text-white/80 leading-relaxed mb-12">
          Cybersecurity is a journey, and we understand enterprise security
          architecture frameworks can be overly complex or demanding for many
          organisations. Our experience with a range of relevant frameworks,
          including TOGAF, COBIT, SABSA, OSA, and the CMMI, allows us to pick
          elements from each that are best suited to your unique organisational
          needs. In turn, we offer you a solution that provides the most value
          without over-complicating the process.
        </p>

        {/* EXPERIENCE */}
        <h2 className="text-xl font-bold mb-4">Our experience</h2>
        <p className="text-white/80 leading-relaxed mb-12">
          Our security architects have a range of experience in different
          industries, organisations, technologies, and roles. They have a range
          of relevant qualifications, from enterprise architecture certificates
          such as TOGAF, to security architecture certifications such as SABSA,
          and cloud platforms, including Azure and AWS.
        </p>

        {/* KEY SERVICES */}
        <h2 className="text-xl font-bold mb-4">Key services</h2>
        <p className="text-white/80 mb-4">
          Our most utilised security architecture services are:
        </p>

        <ul className="list-disc pl-6 space-y-3 text-white/80 mb-14">
          <li>
            <b>Enterprise Security Architecture</b> develops a strategy for the
            secure implementation and management of technology across an entire
            organisation to align with business goals and strategies.
          </li>

          <li>
            <b>Security Solutions Architecture</b> provides technical security
            assistance to security or non-security-focused projects. This could
            involve developing business cases, crafting strategies (such as an
            Identity and Access Management Strategy, a Cloud Security Strategy
            or a Network Security Strategy), providing technical leadership, or
            cybersecurity advisory.
          </li>

          <li>
            <b>Security Architecture Assessment</b> identifies and prioritises
            the risks of a technical system (application, cloud, network,
            infrastructure), including the people, processes, and technology
            involved with that system.
          </li>

          <li>
            <b>Configuration Assessment</b> reviews the implementation and
            configuration of an asset against security industry standards and
            vendor best practices to provide visibility over the vulnerabilities
            within that asset.
          </li>

          <li>
            <b>Security Engineering</b> offers expert support to organisations
            seeking to enhance their cyber security infrastructure by assisting
            in the design, building or uplift of cyber security tools and
            technologies.
          </li>

          <li>
            <b>Security Operations</b> provides assistance to organisations
            seeking to detect and investigate threats and more efficiently
            operate, manage or maintain their cyber security tools and
            technologies.
          </li>
        </ul>

        {/* CTA */}
        <p className="text-white/80 mb-8">
          Get in touch today to discuss creating your next security
          architecture.
        </p>

        <div className="flex justify-center">
          <Link
            to="/contact"
            className="border border-white px-8 py-3 uppercase text-sm tracking-widest hover:bg-white hover:text-[#032d28] transition no-underline"
          >
            Find Out More
          </Link>
        </div>
      </div>
    </section>
  );
}
